import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useMemo, useRef, useState } from "react";

import {
  collectPolygonLocations,
  IMapLibrePolygonItem,
  MapLibrePolygonsLayer,
  useMapLibreLocationsAutoFit,
} from "@ryde/controls";
import { IGeoJSON } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  POLYGON_MAP_DEFAULT_ZOOM,
  POLYGON_MAP_FIT_BOUNDS_PADDING_PX,
  POLYGON_VIEWER_STYLE,
} from "./polygon-map.constants";
import { PolygonViewerStyled } from "./polygon-viewer.styled";

interface IPolygonViewerProps {
  value?: IGeoJSON;
}

/** Single-polygon id — the viewer renders one shape, so it never needs to tell them apart. */
const VIEWER_POLYGON_ID = 0;

/** Read-only polygon preview (import-areas drawer). */
export const PolygonViewer: FC<IPolygonViewerProps> = ({ value }) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const polygons = useMemo<IMapLibrePolygonItem[]>(
    () =>
      value?.features?.length
        ? [{ id: VIEWER_POLYGON_ID, polygon: value, color: rydeColors.red }]
        : [],
    [value],
  );

  const locations = useMemo(
    () => collectPolygonLocations(polygons),
    [polygons],
  );

  useMapLibreLocationsAutoFit({
    mapRef,
    locations,
    isMapLoaded,
    fitPadding: POLYGON_MAP_FIT_BOUNDS_PADDING_PX,
  });

  return (
    <PolygonViewerStyled
      mapRef={mapRef}
      onLoadedChange={setIsMapLoaded}
      initialZoom={POLYGON_MAP_DEFAULT_ZOOM}
    >
      <MapLibrePolygonsLayer
        id="polygon-viewer"
        polygons={polygons}
        style={POLYGON_VIEWER_STYLE}
      />
    </PolygonViewerStyled>
  );
};
